import {
  ApolloClient,
  ApolloLink,
  createHttpLink,
  InMemoryCache,
  type InMemoryCacheConfig,
  type NormalizedCacheObject,
  split,
  type Operation,
  Observable,
} from '@apollo/client'
import { type ErrorLink, onError } from '@apollo/client/link/error'
import { getMainDefinition } from '@apollo/client/utilities'
import { createClient as createWebsocketClient } from 'graphql-ws'
import { GraphQLWsLink } from '@apollo/client/link/subscriptions'
import Cookies from 'js-cookie'
import { isNil } from 'lodash'

export const createClient = ({
  httpUri,
  wsUri,
  errorHandler = () => undefined,
  extraLinks = [],
  cacheConfig,
  isReadOnly = false,
  notifyOnWriteAttempt = () => undefined,
  acceptableMutations = [],
}: {
  httpUri: string
  wsUri: string
  errorHandler?: ErrorLink.ErrorHandler
  extraLinks?: Array<ApolloLink>
  cacheConfig: InMemoryCacheConfig
  isReadOnly?: boolean
  notifyOnWriteAttempt?: () => void
  acceptableMutations?: Array<string>
}): ApolloClient<NormalizedCacheObject> => {
  const httpLink = createHttpLink({ uri: httpUri, credentials: 'include' })

  const errorHandlingLink = onError(response => {
    errorHandler(response)
  })

  const viewOnlyLink = new ApolloLink((operation, forward) => {
    // if we're in view-only mode, do nothing for mutations!
    if (isReadOnly) {
      const definition = getMainDefinition(operation.query)
      if (
        definition.kind === 'OperationDefinition' &&
        definition.operation === 'mutation' &&
        (isNil(definition.name?.value) || !acceptableMutations.includes(definition.name?.value))
      ) {
        return new Observable(observer => {
          notifyOnWriteAttempt()
          observer.next({
            data: null,
          })
          observer.complete()
        })
      }
    }
    return forward(operation)
  })

  const authenticationLink = new ApolloLink((operation, forward) => {
    // No need to manually authenticate requests now that we are using Stytch.
    // Authentication is done via cookie instead of a JWT.
    return forward(operation)
  })

  const wsLink: ApolloLink = new GraphQLWsLink(
    createWebsocketClient({
      url: wsUri,
      connectionParams: {
        sessionToken: Cookies.get('stytch_session'),
      },
    }),
  )

  /**
   * if incoming request is subscription the connection needs to go through websocket (i.e. wsLink)
   *
   * Split enables this behavior:
   * if isSubscription(...) = true then wsLink is used
   * if isSubscription(...) = false then defaultLink is used
   */
  const isSubscription = (operation: Operation): boolean => {
    const definition = getMainDefinition(operation.query)
    return (
      definition.kind === 'OperationDefinition' &&
      definition.operation === 'subscription'
    )
  }

  const defaultLink = ApolloLink.from([
    authenticationLink,
    viewOnlyLink,
    ...extraLinks,
    errorHandlingLink,
    httpLink,
  ])

  const link = split(isSubscription, wsLink, defaultLink)

  const client = new ApolloClient({
    connectToDevTools: process.env.NODE_ENV !== 'production',
    cache: new InMemoryCache(cacheConfig),
    link,
    credentials: 'include',
  })

  return client
}
